import axios, { AxiosRequestConfig } from 'axios';
import { errorHandler, requestErrorHandler } from '../utils/newApplicationUtils';
import { makeSearchParams } from '../utils/utilities';
import { errorAlertDirect } from '../components/ToastMessage';
import { formikErrorHandler } from '../utils/formikUtils';

interface IGetProps {
  pathname: string;
  params?: Record<string, any>;
  formik?: any; // Optional, for form-related error handling
}

const getErrorMessage = (e: any): string => {
  // Check for General error first
  if (e.response?.data?.General?.[0]) {
    return e.response.data.General[0];
  }

  // Check for validation errors in the errors object
  if (e.response?.data?.errors) {
    // Get the first error message from any property in the errors object
    const errorEntries = Object.entries(e.response.data.errors);
    if (errorEntries.length > 0) {
      const [, messages] = errorEntries[0];
      return Array.isArray(messages) ? messages[0] : messages;
    }
  }

  // Check for title in the response
  if (e.response?.data?.title) {
    return e.response.data.title;
  }

  return 'Something went wrong';
};

export const GET = async ({
  pathname,
  params = {},
  formik,
}: IGetProps): Promise<any> => {
  try {
    const paramsSerializer = (params: Record<string, any>): string => makeSearchParams(params);

    const config: AxiosRequestConfig = {
      url: pathname,
      method: 'GET',
      params,
      paramsSerializer,
    };
    const { data } = await axios(config);
    return data;
  } catch (e) {
    console.log('GET error', e.response);

    if (e.response?.status !== 401) {
      errorAlertDirect({
        title: getErrorMessage(e),
        duration: 0,
        closable: true
      });
    }

    if (formik) {
      formikErrorHandler({ formik, data: e.response?.data });
    }

    throw e;
  }
};

interface IPostProps {
  pathname: string;
  body?: Record<string, any>;
  formik?: any; // Optional, for form-related error handling
  params?: Record<string, any>;
}

export const POST = async ({
  pathname,
  body = {},
  params = {},
  formik,
}: IPostProps): Promise<any> => {
  try {
    const paramsSerializer = (params: Record<string, any>): string => makeSearchParams(params);
    const config: AxiosRequestConfig = {
      url: pathname,
      method: 'POST',
      data: body, // POST requests use the `data` field for payload
      params,
      paramsSerializer,
    };

    const { data } = await axios(config);

    return data;
  } catch (e) {
    console.log('POST error', e);

    // Use the direct error alert that doesn't clear existing toasts
    if (e.response?.status !== 401) {
      errorAlertDirect({
        title: getErrorMessage(e),
        duration: 0,
        closable: true
      });
    }

    if (formik) {
      formikErrorHandler({ formik, data: e.response?.data?.errors });
    }

    throw e;
  }
};

interface IPutProps {
  pathname: string;
  body?: Record<string, any> | FormData;
  formik?: any; // Optional, for form-related error handling
  headers?: Record<string, string>; // Optional, for custom headers like multipart/form-data
  params?: Record<string, any>;
}

export const PUT = async ({
  pathname,
  body = {},
  formik,
  headers,
}: IPutProps): Promise<any> => {
  try {
    const config: AxiosRequestConfig = {
      url: pathname,
      method: 'PUT',
      data: body, // PUT requests use the `data` field for payload
    };

    // Add custom headers if provided (e.g., for FormData requests)
    if (headers) {
      config.headers = headers;
    }

    const { data } = await axios(config);

    return data;
  } catch (e) {
    console.log('PUT error', e);

    // Use the direct error alert that doesn't clear existing toasts
    if (e.response?.status !== 401) {
      errorAlertDirect({
        title: getErrorMessage(e),
        duration: 0,
        closable: true
      });
    }

    if (formik) {
      formikErrorHandler({ formik, data: e.response?.data?.errors });
    }

    throw e;
  }
};

interface IDeleteProps {
  pathname: string;
  body?: Record<string, any>;
}

export const DELETE = async ({
  pathname,
  body,
}: IDeleteProps): Promise<any> => {
  try {
    const config: AxiosRequestConfig = {
      url: pathname,
      method: 'DELETE',
      data: body,
    };

    const { data } = await axios(config);

    return data;
  } catch (e) {
    console.log('DELETE error', e);

    // Use the direct error alert that doesn't clear existing toasts
    if (e.response?.status !== 401) {
      errorAlertDirect({
        title: getErrorMessage(e),
        duration: 0,
        closable: true
      });
    }

    throw e;
  }
};

interface IHateoasProps {
  actionData: any;
  pathname: string;
  body?: Record<string, any>;
  formik?: any; // Optional, for form-related error handling
}

export const hateoasRequest = async ({ actionData, pathname, body = {}, formik, }: IHateoasProps): Promise<any> => {
  try {

    let configData = {};

    if (actionData?.fields) {
      (actionData.fields || []).forEach((field: any) => {
        configData[field.name] = field.value;
      });
    } else if (body) {
      configData = body;
    }

    const config: AxiosRequestConfig = {
      url: `${pathname}${actionData?.href}`,
      method: actionData?.method,
      data: configData,
    };

    const { data } = await axios(config);

    return data;
  } catch (error) {
    if (formik) {
      errorHandler({ e: error, formik });
    } else {
      requestErrorHandler(error);
    }
    throw error;
  }
};
